import { CalendarIcon, SearchIcon, StopwatchIcon, SyncIcon, XIcon } from '@primer/octicons-react';
import { useRouter } from 'next/router';
import React, { ComponentProps, Suspense } from 'react';
import { graphql } from 'react-relay';

import Button from '../../components/core/Button';
import CheckboxGroupField from '../../components/core/CheckboxGroupField';
import DateRangeField from '../../components/core/DateRangeField';
import DialogButton from '../../components/core/DialogButton';
import EmptyState from '../../components/core/EmptyState';
import EnumPairSearchOverlayTokenField from '../../components/core/EnumPairSearchOverlayTokenField';
import ErrorBoundary from '../../components/core/ErrorBoundary';
import Grid from '../../components/core/Grid';
import Head from '../../components/core/Head';
import IconButton from '../../components/core/IconButton';
import { HeaderSidebarNavPageLayout } from '../../components/core/Layout';
import QueryFormik from '../../components/core/QueryFormik';
import Spinner from '../../components/core/Spinner';
import Stack from '../../components/core/Stack';
import Text from '../../components/core/Text';
import Tooltip from '../../components/core/Tooltip';
import View from '../../components/core/View';
import PromotionConnectionDataTable from '../../components/promotion/PromotionConnectionDataTable';
import PromotionCreateDialog from '../../components/promotion/PromotionCreateDialog';
import PromotionWindowPaginator from '../../components/promotion/PromotionWindowPaginator';
import UserPaginatorSearchOverlayTokenField from '../../components/user/UserPaginatorSearchOverlayTokenField';
import { usePaginationContext } from '../../contexts/PaginationContext';
import useInitialValuesFromParsedUrlQuery from '../../hooks/useInitialValuesFromParsedUrlQuery';
import useToast from '../../hooks/useToast';
import {
  promotion_promotionsQuery,
  PromotionStatusEnum,
} from '../../relay/__generated__/promotion_promotionsQuery.graphql';
import { numberWithCommas } from '../../utils/number';
import { NextPage } from '../_app';

import PromotionId from './[promotionId]';

const promotionsForPromotion = graphql`
  query promotion_promotionsQuery($filters: PromotionFilter, $order: PromotionOrder, $page: Int, $pageSize: Int) {
    ...PromotionWindowPaginator_query @arguments(filters: $filters, order: $order, page: $page, pageSize: $pageSize)
  }
`;

type Props = {};

const Promotion: NextPage<Props> = () => {
  const router = useRouter();
  const { toast } = useToast();
  const { initialValues, setParsedUrlQuery } = useInitialValuesFromParsedUrlQuery({
    search: { type: 'string' },
    order: { type: 'string' },
    page: { type: 'number' },

    status_In: { type: 'string', multiple: true },
    createdById_In: { type: 'string', multiple: true },

    isRepeat_In: { type: 'boolean', multiple: true },

    endAt_Lte: { type: 'string' },
    startAt_Gte: { type: 'string' },
  });
  const { search, order, page, status_In, createdById_In, endAt_Lte, startAt_Gte, isRepeat_In } = initialValues;

  const { pageSize } = usePaginationContext();

  return (
    <View>
      <Head siteTitle={'프로모션'} />
      <ErrorBoundary>
        <QueryFormik<promotion_promotionsQuery>
          query={promotionsForPromotion}
          staticVariables={{ pageSize }}
          initialValues={{
            filters: {
              search,
              createdById_In,
              status_In: status_In as PromotionStatusEnum[],
              endAt_Lte,
              startAt_Gte,
              isRepeat_In,
            },
            order: order || '-startAt',
            page,
          }}
          options={{ fetchPolicy: 'store-and-network' }}
          onSubmit={(values) => setParsedUrlQuery({ ...values.filters, order }, { scroll: false })}
          enableReinitialize
        >
          {({ values: { filters, order }, handleSubmit, resetForm, setFieldValue, submitForm }, queryReference) => {
            const handleReset = () => {
              resetForm({ values: { order } });
              setParsedUrlQuery({ order }, { scroll: false });
            };

            const handleChangeOrderActionMenu: ComponentProps<typeof QueryFormik.OrderActionMenuButton>['onChange'] = (
              newValue,
            ) => {
              setParsedUrlQuery({ ...filters, order: newValue }, { scroll: false });
            };

            const isPeriodSelected = filters?.startAt_Gte || filters?.endAt_Lte;

            return (
              <View>
                <Grid sx={{ alignItems: 'center' }} gapX={2}>
                  <Grid.Unit size={'max'}>
                    <Text as={'h1'}>프로모션</Text>
                  </Grid.Unit>
                  <Grid.Unit size={'min'}>
                    <Tooltip text={'룰루의 요술 달력'}>
                      <IconButton
                        size={'large'}
                        aria-label={'룰루의 요술 달력'}
                        variant={'default'}
                        icon={CalendarIcon}
                        onClick={() => {
                          window.open(`${process.env.NEXT_PUBLIC_TCMS_END_POINT}promotions/calendar`, '_blank');
                        }}
                      />
                    </Tooltip>
                  </Grid.Unit>
                  <Grid.Unit size={'min'}>
                    <DialogButton
                      size={'large'}
                      renderDialog={({ isOpen, closeDialog }) => (
                        <PromotionCreateDialog
                          isOpen={isOpen}
                          onDismiss={closeDialog}
                          full
                          config={{
                            onCompleted: () => {
                              toast('프로모션 생성이 완료됐어요', 'success');
                              handleSubmit();
                              closeDialog();
                            },
                            onError: () => {
                              toast('다시 생성해 주세요', 'error');
                            },
                          }}
                        />
                      )}
                      variant={'primary'}
                    >
                      생성하기
                    </DialogButton>
                  </Grid.Unit>
                </Grid>
                <Grid>
                  <Grid.Unit size={'min'}>
                    <QueryFormik.FilterSearchTextField
                      typename={'PromotionFilter'}
                      label={'Search'}
                      labelConfig={{ visuallyHidden: true }}
                      name={'filters.search'}
                      size={'large'}
                      autoComplete={'off'}
                      leadingVisual={SearchIcon}
                      debounce
                      onChange={(e) => {
                        setFieldValue('filters.search', e.target.value);
                        setTimeout(() => submitForm(), 0);
                      }}
                    />
                  </Grid.Unit>
                  <Grid.Unit size={'max'}>
                    <View sx={{ display: 'flex', justifyContent: 'end' }}>
                      <Stack gapX={2}>
                        <Stack.Item>
                          <Button leadingIcon={SyncIcon} variant={'plain'} onClick={handleReset}>
                            초기화
                          </Button>
                        </Stack.Item>
                        <Stack.Item>
                          <QueryFormik.OrderActionMenuButton
                            typename={'PromotionOrder'}
                            orders={['startAt']}
                            onChange={handleChangeOrderActionMenu}
                          />
                        </Stack.Item>
                      </Stack>
                    </View>
                  </Grid.Unit>
                </Grid>
                <View sx={{ marginTop: 5 }}>
                  <QueryFormik.FilterGrid
                    onChange={(filters) => {
                      setParsedUrlQuery({ ...filters, order }, { scroll: false });
                    }}
                  >
                    <QueryFormik.FilterGridUnit size={[1, 1, 1 / 2]} title={'상태'}>
                      <EnumPairSearchOverlayTokenField
                        typename={'PromotionStatusEnum'}
                        label={'상태'}
                        name={'filters.status_In'}
                        placeholder={'상태 선택'}
                      />
                    </QueryFormik.FilterGridUnit>
                    <QueryFormik.FilterGridUnit size={[1, 1, 1 / 2]} title={'반복'}>
                      <CheckboxGroupField
                        name={'filters.isRepeat_In'}
                        label={'반복'}
                        options={[
                          { id: 'true', text: 'O' },
                          { id: 'false', text: 'X' },
                        ]}
                        renderContainer={(children) => (
                          <View sx={{ paddingX: 2 }}>
                            <Grid gapX={2}>{children}</Grid>
                          </View>
                        )}
                        renderOptionWrapper={(children, { id }) => (
                          <Grid.Unit key={id} size={'min'}>
                            {children}
                          </Grid.Unit>
                        )}
                        value={filters?.isRepeat_In?.map((value) => (value === true ? 'true' : 'false'))}
                        onChange={(selected) =>
                          setFieldValue(
                            'filters.isRepeat_In',
                            selected.map((selectedValue) => (selectedValue === 'true' ? true : false)),
                          )
                        }
                      />
                    </QueryFormik.FilterGridUnit>
                    <QueryFormik.FilterGridUnit size={[1, 1, 1 / 2]} title={'제작자'}>
                      <UserPaginatorSearchOverlayTokenField
                        label={'제작자'}
                        placeholder={'제작자 선택'}
                        name={'filters.createdById_In'}
                      />
                    </QueryFormik.FilterGridUnit>
                    <QueryFormik.FilterGridUnit size={[1, 1, 1 / 2]} title={'진행일'}>
                      <Grid sx={{ alignItems: 'center' }} wrap={false}>
                        <Grid.Unit size={'max'}>
                          <DateRangeField
                            label={'진행일'}
                            labelConfig={{ visuallyHidden: true }}
                            name={['filters.startAt_Gte', 'filters.endAt_Lte']}
                            placeholder={['시작일', '종료일']}
                          />
                        </Grid.Unit>
                        <Grid.Unit size={'min'}>
                          <IconButton
                            icon={XIcon}
                            aria-label={'reset created period'}
                            variant={'plain'}
                            disabled={!isPeriodSelected}
                            onClick={() => {
                              setFieldValue('filters', { ...filters, startAt_Gte: undefined, endAt_Lte: undefined });
                            }}
                          />
                        </Grid.Unit>
                      </Grid>
                    </QueryFormik.FilterGridUnit>
                  </QueryFormik.FilterGrid>
                </View>
                <View sx={{ marginTop: 5 }}>
                  <ErrorBoundary key={queryReference?.fetchKey}>
                    <Suspense fallback={<Spinner />}>
                      <QueryFormik.PreloadedQueryRenderer<promotion_promotionsQuery>>
                        {(queryReference) => (
                          <PromotionWindowPaginator
                            fragmentReference={queryReference}
                            onLoadPage={(page) => setParsedUrlQuery({ ...filters, order, page })}
                          >
                            {({ promotions }, { renderPagination }) => (
                              <>
                                <View>
                                  <Text sx={{ fontSize: 1, fontWeight: 'bold', color: 'fg.muted' }}>
                                    총 {numberWithCommas(promotions?.totalCount || 0)}건
                                  </Text>
                                </View>
                                <View sx={{ marginTop: 3 }}>
                                  <PromotionConnectionDataTable
                                    promotionConnection={promotions}
                                    onRowClick={({ id }) => router.push({ pathname: `/promotion/${id}` })}
                                    emptyState={
                                      <View sx={{ paddingY: 3 }}>
                                        <EmptyState
                                          title={'필터에 맞는 결과가 없어요'}
                                          description={'다른 필터로 다시 시도해보세요.'}
                                        />
                                      </View>
                                    }
                                  />
                                </View>
                                <View sx={{ display: 'flex', justifyContent: 'center', marginTop: 3 }}>
                                  {renderPagination?.()}
                                </View>
                              </>
                            )}
                          </PromotionWindowPaginator>
                        )}
                      </QueryFormik.PreloadedQueryRenderer>
                    </Suspense>
                  </ErrorBoundary>
                </View>
              </View>
            );
          }}
        </QueryFormik>
      </ErrorBoundary>
    </View>
  );
};

Promotion.getLayout = (page) => <HeaderSidebarNavPageLayout>{page}</HeaderSidebarNavPageLayout>;
Promotion.authenticated = true;
Promotion.routes = [
  {
    id: 'promotion',
    icon: StopwatchIcon,
    pathname: '/promotion',
    name: '프로모션',
    permissions: ['promotion_read'],
  },
  ...PromotionId.routes,
];

export default Promotion;
